import type {
  SearchOptions,
  SearchResults,
  SearchSuggestion,
  SearchSuggestions,
} from '@/homescreen/search/omniSearch.types';
import { errorToast } from '@/utilities/toast.utilities';
import { nanosToMillis } from '@/datetime/dateTime.utilities';
import type { HomeScreenItemType } from '@/homescreen/HomeScreenTable.organism';
import moment from 'moment';
import { normalizeTypes } from '@/homescreen/homescreen.actions';
import type { SearchItemLocation } from '@/main/app.constants';
import { SEARCH_ITEM_LOCATIONS } from '@/main/app.constants';
import { LocationEnum, SortFieldEnum, SortOrderEnum, sqFoldersApi } from '@/sdk/api/FoldersApi';
import type { FolderSearchResultsV1 } from '@/sdk/model/FolderSearchResultsV1';
import { HOME_SCREEN_SORT } from '@/homescreen/homescreen.constants';

const LOCATION_TO_FILTER: Partial<Record<SearchItemLocation, LocationEnum>> = {
  [SEARCH_ITEM_LOCATIONS.ALL]: LocationEnum.ALL,
  [SEARCH_ITEM_LOCATIONS.USERS]: LocationEnum.USERS,
  [SEARCH_ITEM_LOCATIONS.CORPORATE]: LocationEnum.CORPORATE,
  [SEARCH_ITEM_LOCATIONS.MY_FOLDER]: LocationEnum.MINE,
  [SEARCH_ITEM_LOCATIONS.SHARED_OR_PUBLIC]: LocationEnum.SHARED,
} as const;

const SORT_FIELD_TO_SORT_ENUM = {
  [HOME_SCREEN_SORT.CREATED_AT]: SortFieldEnum.CreatedAt,
  [HOME_SCREEN_SORT.UPDATED_AT]: SortFieldEnum.UpdatedAt,
  [HOME_SCREEN_SORT.OWNER]: SortFieldEnum.Owner,
  [HOME_SCREEN_SORT.CREATOR]: SortFieldEnum.Creator,
  [HOME_SCREEN_SORT.NAME]: SortFieldEnum.Name,
} as const;

export async function getMeilisearchSuggestions(options: SearchOptions): Promise<SearchSuggestions> {
  const results = await search(options);
  return { suggestions: results ? mapToSuggestions(results) : [], facets: results?.facetDistribution ?? {} };
}

export async function getMeilisearchResults(options: SearchOptions): Promise<SearchResults> {
  const results = await search(options);

  return {
    results: mapToHomeScreenItems(results),
    facets: results.facetDistribution,
    path: results.path,
    totalResults: results.estimatedTotalHits,
  };
}

async function search(options: SearchOptions): Promise<FolderSearchResultsV1> {
  const { params, config, abortSignal } = options;
  const filters = [];
  const types = normalizeTypes(params.types);
  if (types) {
    filters.push(`Type IN [${types.map((type) => `"${type}"`).join(', ')}]`);
  }
  if (params.ownerIds && params.ownerIds.length > 0) {
    filters.push(`Owner IN [${params.ownerIds.map((id) => `"${id}"`).join(', ')}]`);
  }
  if (params.creatorIds && params.creatorIds.length > 0) {
    filters.push(`Creator IN [${params.creatorIds.map((id) => `"${id}"`).join(', ')}]`);
  }
  const filter = filters.join(' AND ');

  const folderLocation = Object.values(SEARCH_ITEM_LOCATIONS).find((value) => value === params.folderId);
  const parentFolderId = !folderLocation ? params.folderId : undefined;
  const maybeLocation = (params.filter ?? folderLocation) as SearchItemLocation | undefined;
  let location = maybeLocation && LOCATION_TO_FILTER[maybeLocation] ? LOCATION_TO_FILTER[maybeLocation] : undefined;
  if (params.isArchived) {
    location = LocationEnum.TRASH;
  }

  let sortField: SortFieldEnum | undefined;
  let sortOrder: SortOrderEnum | undefined;
  if (params.sortOrder) {
    const [field, direction] = params.sortOrder.split(' ');
    sortField = SORT_FIELD_TO_SORT_ENUM[field] ? SORT_FIELD_TO_SORT_ENUM[field] : undefined;
    sortOrder = direction === 'asc' ? SortOrderEnum.ASC : SortOrderEnum.DESC;
  }

  try {
    const { data } = await sqFoldersApi.search(
      {
        query: params.textSearch,
        filter,
        location,
        parentFolderId,
        sortField,
        sortOrder,
        hitsPerPage: config.resultsPerPage,
        page: config.page,
        hitHighlightFormat: config.highlightFormat,
      },
      { signal: abortSignal },
    );

    return data;
  } catch (error) {
    errorToast({ httpResponseOrError: error });
    return {
      hits: [],
      facetDistribution: {},
      estimatedTotalHits: 0,
      hitsPerPage: 0,
      page: 1,
    };
  }
}

function mapToSuggestions(results: FolderSearchResultsV1): SearchSuggestion[] {
  return results.hits.map((hit: any) => {
    const content = hit.Content;
    return {
      seeqId: content.ID,
      name: content.Name,
      owner: content.Owner ?? '',
      creator: content.Creator ?? '',
      highlightedName: hit.Highlights.Name ?? content.Name ?? 'Unnamed Item',
      contentType: content.Type ?? '',
      createdAt: nanosToMillis(content['Created At']),
      updatedAt: nanosToMillis(content['Updated At']),
    };
  });
}

function mapToHomeScreenItems(results: FolderSearchResultsV1): HomeScreenItemType[] {
  return results.hits.map((hit: any) => {
    const content = hit.Content;
    return {
      id: content.ID,
      name: content.Name,
      createdAt: moment.utc(nanosToMillis(content['Created At'])).toISOString(),
      updatedAt: moment.utc(nanosToMillis(content['Updated At'])).toISOString(),
      owner: { name: content.Owner, isRedacted: false },
      creator: { name: content.Creator, isRedacted: false },
      isLocked: content.Locked,
      type: content.Type,
    };
  });
}
